import { Box, Modal } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import ButtonCloseModal from '@/components/ui/Button/ButtonCloseModal';
import Pagination from '@/components/ui/Pagination/Pagination';
import Loader from '@/components/ui/Loader/Loader';
import ModalStyle from '@/constants/ModalStyle';
import { isIphone } from '@/helper/isIphone';

import usePatch from '@/hooks/usePatch';
import file from '@/api/file';

const { getAttachments, getPublicAttachments } = file();

const downloadFileFromSource = async (source, fName) => {
	try {
		const response = await fetch(source);

		if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);

		const blob = await response.blob();

		const blobUrl = window.URL.createObjectURL(blob);

		if (navigator.userAgent.includes('Safari') && isIphone()) {
			window.open(source, '_blank');
		} else {
			const a = document.createElement('a');
			a.href = blobUrl;
			a.download = fName || 'downloaded-file';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(blobUrl);
		}
	} catch (error) {
		toast.error(`Failed to download file: ${error.message}`);
	}
};

export default function Files(props) {
	const { open, onClose, id, project, from } = props;
	const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

	// const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
	// const [rowSelectionModel, setRowSelectionModel] = useState([]);
	// const [rowSelected, setRowSelected] = useState([]);
	const [columnVisibility, setColumnVisibility] = useState({});
	// const [fileSizeDownload, setFileSizeDownload] = useState(null);
	// const [showPopup, setShowPopup] = useState(false);
	// const [fileNotExist, setFileNotExist] = useState([]);
	const [isLoading, setLoading] = useState(false);
	const apiRef = useGridApiRef();

	const isProject = from === 'project';

	const { data: d, isFetching: isFetchingD } = useQuery({
		queryKey: ['file', id],
		queryFn: async () => {
			try {
				const result = isProject ? await getAttachments(id) : await getPublicAttachments(id, { public: 1 });
				return result;
			} catch (error) {
				return error;
			}
		},
		enabled: !!id, // Only run query if id exists
	});

	const docs =
		useMemo(() => {
			const res = d?.data?.attachments;
			if (typeof res === 'string') {
				return [];
			}
			const result = d?.data?.attachments || [];
			return result;
		}, [d]) || [];

	const handleClose = () => {
		onClose();
	};

	const {
		mutate: postDownload,
		isPending,
		isSuccess,
	} = usePatch({
		url: isAuthenticated ? `downloadlink` : `downloadlink/?public=1`,
		method: 'post',
		type: 'form-data',
		isAuth: !!isAuthenticated,
		callback: async (res) => {
			if (res.status === 200 && res.data?.download_url) {
				setLoading(true);
				await downloadFileFromSource(res.data.download_url, res.data.downloadlog?.filedownload);
				setLoading(false);
			} else {
				toast.error('Invalid download response from server');
			}
		},
		onError: (err) => {
			toast.error(`${err.status} - ${err.statusText}`);
		},
	});

	// const {
	// 	mutate: zipDownload,
	// 	isPending: isPendingZip,
	// 	isSuccess: isSuccessZip,
	// } = usePatch({
	// 	url: `downloadlink`,
	// 	method: 'post',
	// 	type: 'form-data',
	// 	// responseType: 'arraybuffer',
	// 	callback: async (res) => {
	// 		setRowSelectionModel([]);
	// 		setRowSelected([]);
	// 		try {
	// 			const blob = base64ToBlob(res.data.zip_file, 'application/zip');

	// 			const fileSizeMB = (blob.size / (1024 * 1024)).toFixed(2);

	// 			if (res.data.error_file?.length) {
	// 				setFileNotExist(res.data.error_file);
	// 			}
	// 			setFileSizeDownload(`${fileSizeMB}MB`);
	// 			setShowPopup(true);

	// 			const pType = project.productprefix_detail?.length ? project.productprefix_detail[0]?.productprefix : project.productprefix || '';

	// 			const link = document.createElement('a');
	// 			link.href = URL.createObjectURL(blob);
	// 			link.download = `${pType} ${project.productcodenumber} ${project.productname} ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}.zip`;
	// 			document.body.appendChild(link);

	// 			setTimeout(() => {
	// 				link.click();
	// 				document.body.removeChild(link);

	// 				setTimeout(() => {
	// 					setShowPopup(false);
	// 					setFileSizeDownload(null);
	// 					setFileNotExist([]);
	// 				}, 3000);
	// 			}, 100);
	// 			// eslint-disable-next-line no-empty
	// 		} catch (error) {}
	// 	},
	// 	onError: (err) => {
	// 		setRowSelectionModel([]);
	// 		setRowSelected([]);
	// 		toast.error(`${err.status} - ${err.statusText}`);
	// 	},
	// });

	const onDownload = (data) => {
		if (!data.linkdatasheet.includes('http')) {
			const postDownloadFormat = {
				productid: id,
				attachments: JSON.stringify(data),
			};
			postDownload(postDownloadFormat);
		} else {
			downloadFileFromSource(data.linkdatasheet);
		}
	};

	// const onZipDownload = () => {
	// 	if (rowSelected.length) {
	// 		const postDownloadFormat = {
	// 			productid: id,
	// 			attachments: JSON.stringify(rowSelected),
	// 			zip_file: true,
	// 		};
	// 		zipDownload(postDownloadFormat);
	// 	}
	// };

	const columns = [
		{
			field: 'linkdatasheet',
			headerName: 'Filnavn',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
			renderCell: (d) => `${d.row.linkdatasheet}`,
		},
		{
			field: 'timeupdate',
			headerName: 'Tidsoppdatering',
			headerAlign: 'center',
			align: 'center',
			renderCell: (d) => format(new Date(d.row.timeupdate), 'yyyy-MM-dd'),
			minWidth: 120,
		},
		{
			field: 'datasheetid',
			headerName: '',
			headerAlign: 'center',
			align: 'center',
			filterable: false,
			disableColumnMenu: true,
			renderCell: (d) => {
				return (
					<button type='button' className='flex items-center justify-center h-full cursor-pointer' onClick={() => onDownload(d.row)}>
						<Download />
					</button>
				);
			},
		},
	];

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth < 768) {
				setColumnVisibility({ linkdatasheet: true, timeupdate: false, datasheetid: true }); // Hide these columns on small screens
			} else {
				setColumnVisibility({ linkdatasheet: true, timeupdate: true, datasheetid: true }); // Show them on larger screens
			}
		};

		window.addEventListener('resize', handleResize);
		handleResize(); // Call initially
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const pType =
		project.component_prefix_name ||
		(project.productprefix_detail ? project.productprefix_detail[0]?.productprefix : '') ||
		project.producttype ||
		project.productprefix ||
		'';
	const pNumber = project.component_code_number || project.productcodenumber || '';

	return (
		<Modal open={open} onClose={handleClose} aria-labelledby='modal-files' aria-describedby='modal-files' className='w-full'>
			<Box sx={{ ...ModalStyle, top: '50%' }} className='flex flex-col gap-4 w-[90%] md:w-[1020px] max-h-[90%]'>
				<span className='text-2xl modal-title'>
					Datablad ({pType} - {pNumber})
				</span>
				<ButtonCloseModal onClose={handleClose} />

				<hr />

				<div className='relative flex flex-col w-full gap-4 overflow-x-auto'>
					<div className='relative flex flex-col w-full h-full gap-4 overflow-x-auto'>
						{((isPending && !isSuccess) || isLoading) && <Loader />}
						{/* {fileSizeDownload && showPopup && (
							<div className='absolute z-50 h-full -translate-x-1/2 top-1/2 left-1/2'>
								<span className=' animate-pulse'>Downloading {fileSizeDownload} file</span>
							</div>
						)} */}
						<DataGrid
							apiRef={apiRef}
							autoHeight
							rows={docs || []}
							loading={isFetchingD}
							columns={columns}
							className='flex-1 w-full mx-auto mt-0'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								// if (fileNotExist.includes(params.row.datasheetid)) {
								// 	cellClass += ' not-exist';
								// }
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
							}}
							initialState={{}}
							getRowId={(row) => row.linkdatasheet + row.timeupdate}
							// onRowSelectionModelChange={(newRowSelectionModel) => {
							// 	const selectedData = newRowSelectionModel.map((id) => apiRef.current.getRow(id));

							// 	setRowSelected(selectedData);
							// 	setRowSelectionModel(newRowSelectionModel);
							// }}
							// checkboxSelection
							// rowSelectionModel={rowSelectionModel}
							columnVisibilityModel={columnVisibility}
							onColumnVisibilityModelChange={(newModel) => setColumnVisibility(newModel)}
						/>
						{/* <div className='absolute z-10 top-2 left-14'>
							<button className='w-auto btn' onClick={onZipDownload} disabled={!rowSelectionModel.length || rowSelectionModel.length < 2}>
								Zip Download
							</button>
						</div> */}
					</div>
				</div>
			</Box>
		</Modal>
	);
}
